import React from "react";
import { motion } from "framer-motion";
import { Avatar, Box, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { blue, green, grey, red } from "@mui/material/colors";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";

import { formatFinance } from "../../../../utils/formatters";

const ComplementItem = styled(motion.div)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  backgroundColor: alpha(blue[50], 0.2),
  border: `1px solid ${alpha(blue[100], 0.4)}`,
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1.5),
  display: "flex",
  flexDirection: "column",
  transition: "all 0.2s ease",
}));

const ActionButton = styled(motion.button)(({ theme, color = "blue" }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor:
    color === "red"
      ? alpha(red[100], 0.3)
      : color === "green"
      ? alpha(green[100], 0.3)
      : alpha(blue[100], 0.3),
  color:
    color === "red" ? red[600] : color === "green" ? green[600] : blue[600],
  border: `1px solid ${
    color === "red"
      ? alpha(red[200], 0.3)
      : color === "green"
      ? alpha(green[200], 0.3)
      : alpha(blue[200], 0.3)
  }`,
  borderRadius: "50%",
  width: 32,
  height: 32,
  cursor: "pointer",
  padding: 0,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor:
      color === "red"
        ? alpha(red[100], 0.5)
        : color === "green"
        ? alpha(green[100], 0.5)
        : alpha(blue[100], 0.5),
  },
}));

const QuantityBadge = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(blue[600], 0.1),
  color: blue[700],
  fontWeight: 600,
  fontSize: "0.85rem",
  padding: theme.spacing(0.5, 1.2),
  minWidth: 32,
  textAlign: "center",
  borderRadius: theme.spacing(0.8),
  border: `1px solid ${alpha(blue[200], 0.3)}`,
}));

const PriceContainer = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(green[50], 0.5),
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  color: green[700],
  fontSize: "0.9rem",
  border: `1px solid ${alpha(green[100], 0.5)}`,
}));

const ProductComplementListItem = ({
  complement,
  increaseQuantity,
  decreaseQuantity,
  remove,
  index,
}) => {
  const handleIncreaseQuantity = (event) => {
    event.stopPropagation();
    increaseQuantity(complement);
  };

  const handleDecreaseQuantity = (event) => {
    event.stopPropagation();
    decreaseQuantity(complement);
  };

  const handleRemove = (event) => {
    event.stopPropagation();
    remove(complement.complement);
  };

  return (
    <ComplementItem
      whileHover={{ y: -2 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: alpha(blue[600], 0.1),
              color: blue[700],
            }}
          >
            <LocalCafeIcon fontSize="small" />
          </Avatar>
          <Typography variant="body1" fontWeight={500} color={grey[800]}>
            {complement.title}
          </Typography>
        </Box>

        <ActionButton
          color="red"
          onClick={handleRemove}
          component={motion.button}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </ActionButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ActionButton
            onClick={handleDecreaseQuantity}
            component={motion.button}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <RemoveIcon fontSize="small" />
          </ActionButton>

          <QuantityBadge>{complement.quantity}</QuantityBadge>

          <ActionButton
            color="green"
            onClick={handleIncreaseQuantity}
            component={motion.button}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <AddIcon fontSize="small" />
          </ActionButton>
        </Box>

        <PriceContainer>
          {formatFinance(complement.price * complement.quantity)}
        </PriceContainer>
      </Box>
    </ComplementItem>
  );
};

export default ProductComplementListItem;
